/**
 * Homeowner view of past monthly reports.
 * Renders report_data JSONB inline using the same visual structure as the email.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ChevronDown, ChevronUp, Sun, Home, Zap, DollarSign } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatCurtailmentDollars } from '@/components/dashboard/CurtailmentCard';

interface ReportHistoryRow {
  id: string;
  period_start: string;
  period_end: string;
  report_data: ReportData;
  sent_at: string;
  status: string;
}

interface ReportData {
  property_name: string;
  period: { month_label: string };
  energy_summary: {
    total_pv_produced_kwh: number;
    total_consumed_kwh: number;
    self_supply_percent: number;
    grid_imported_kwh: number;
    grid_exported_kwh: number;
  };
  savings: {
    estimated_savings_dollars: number;
    vs_grid_only_dollars: number;
    rate_cents_per_kwh: number;
  };
  battery_summary: {
    charge_throughput_kwh: number;
    discharge_throughput_kwh: number;
    degradation_percent: number;
  } | null;
  curtailment: {
    total_kwh_wasted: number;
    total_dollars_wasted: number;
  };
  service_activity: {
    visits_completed: number;
    issues_resolved: number;
  };
  anomalies: {
    critical_count: number;
    warning_count: number;
    notable_events: { title: string; severity: string }[];
  };
}

function StatBox({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex flex-col items-center p-3 rounded-md bg-muted/20">
      {icon}
      <span className="text-lg font-bold tabular-nums mt-1">{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function ReportViewer({ data }: { data: ReportData }) {
  return (
    <div className="space-y-4 pt-3">
      {/* Energy stats */}
      <div className="grid grid-cols-3 gap-2">
        <StatBox
          icon={<Sun className="w-4 h-4 text-wt-amber-primary" />}
          label="Solar"
          value={`${data.energy_summary.total_pv_produced_kwh.toFixed(0)} kWh`}
        />
        <StatBox
          icon={<Home className="w-4 h-4 text-muted-foreground" />}
          label="Usage"
          value={`${data.energy_summary.total_consumed_kwh.toFixed(0)} kWh`}
        />
        <StatBox
          icon={<Zap className="w-4 h-4 text-wt-green-primary" />}
          label="Self-Powered"
          value={`${data.energy_summary.self_supply_percent.toFixed(0)}%`}
        />
      </div>

      {/* Savings */}
      <div className="rounded-md bg-wt-green-primary/10 border border-wt-green-primary/20 p-3 text-center">
        <DollarSign className="w-5 h-5 text-wt-green-primary mx-auto mb-1" />
        <p className="text-xl font-bold text-wt-green-primary">
          {formatCurtailmentDollars(data.savings.estimated_savings_dollars)}
        </p>
        <p className="text-xs text-muted-foreground">estimated savings this month</p>
      </div>

      {/* Battery */}
      {data.battery_summary && (
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground text-sm">Battery</p>
          <p>
            Charged {data.battery_summary.charge_throughput_kwh.toFixed(0)} kWh /
            Discharged {data.battery_summary.discharge_throughput_kwh.toFixed(0)} kWh
          </p>
        </div>
      )}

      {/* Curtailment */}
      {data.curtailment.total_kwh_wasted > 0 && (
        <div className="text-xs text-muted-foreground">
          <p className="font-medium text-foreground text-sm">Curtailment</p>
          <p>
            {data.curtailment.total_kwh_wasted.toFixed(1)} kWh curtailed
            (~{formatCurtailmentDollars(data.curtailment.total_dollars_wasted)})
          </p>
        </div>
      )}

      {/* Service */}
      {(data.service_activity.visits_completed > 0 || data.service_activity.issues_resolved > 0) && (
        <div className="text-xs text-muted-foreground">
          <p className="font-medium text-foreground text-sm">Service</p>
          <p>
            {data.service_activity.visits_completed > 0 && `${data.service_activity.visits_completed} visit${data.service_activity.visits_completed > 1 ? 's' : ''}`}
            {data.service_activity.visits_completed > 0 && data.service_activity.issues_resolved > 0 && ', '}
            {data.service_activity.issues_resolved > 0 && `${data.service_activity.issues_resolved} issue${data.service_activity.issues_resolved > 1 ? 's' : ''} resolved`}
          </p>
        </div>
      )}
    </div>
  );
}

interface MonthlyReportsListProps {
  propertyId: string;
}

export function MonthlyReportsList({ propertyId }: MonthlyReportsListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: reports, isLoading } = useQuery({
    queryKey: ['monthly-reports', propertyId],
    queryFn: async (): Promise<ReportHistoryRow[]> => {
      const { data, error } = await supabase
        .from('customer_report_history')
        .select('id, period_start, period_end, report_data, sent_at, status')
        .eq('property_id', propertyId)
        .eq('status', 'sent')
        .order('sent_at', { ascending: false })
        .limit(12);
      if (error) throw error;
      return (data ?? []) as unknown as ReportHistoryRow[];
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-4">
          <div className="h-12 rounded bg-muted/30 animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  if (!reports || reports.length === 0) return null;

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <FileText className="h-4 w-4" />
          Monthly Reports
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {reports.map((r) => {
          const isExpanded = expandedId === r.id;
          const monthLabel = new Date(r.period_start).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          });
          return (
            <div key={r.id}>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between text-xs h-auto py-2 px-2"
                onClick={() => setExpandedId(isExpanded ? null : r.id)}
              >
                <span className="flex items-center gap-2">
                  <FileText className="w-3 h-3 text-muted-foreground" />
                  {monthLabel}
                </span>
                <span className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] text-green-400 border-green-400/30">
                    sent
                  </Badge>
                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </span>
              </Button>
              {isExpanded && r.report_data && (
                <div className="px-2 pb-2">
                  <ReportViewer data={r.report_data} />
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
